import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Loader2, Copy, Check, ExternalLink, Share2, Mail, Code,
} from 'lucide-react';
import { BrandedVideoPlayer } from './BrandedVideoPlayer';
import { useBrandedViewer } from './BrandedViewerProvider';
import type { AnalyticsHandler } from './types';

interface BrandedViewerLayoutProps {
  isAdmin?: boolean;
  onAnalytics: AnalyticsHandler;
}

const SHARE_LOGO_CLASS =
  'h-[38px] w-auto max-w-[12rem] object-contain flex-shrink-0 sm:h-[43px] sm:max-w-[14.4rem]';

const SOCIAL_ICONS = {
  whatsapp: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
};

export function BrandedViewerLayout({ isAdmin, onAnalytics }: BrandedViewerLayoutProps) {
  const {
    config, video,
    effectivePrimary, effectiveSecondary,
    effectiveLogoUrl, effectiveCtaLabel, effectiveCtaUrl,
    showSocialShare, agencyName,
  } = useBrandedViewer();

  // Per-video orientation wins over the app's default aspect mode.
  const aspectMode = video.orientation ?? config.aspectMode;

  const [linkCopied, setLinkCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const viewRecorded = useRef(false);
  const playRecorded = useRef(false);
  const completeRecorded = useRef(false);
  const cumulativeWatchMs = useRef(0);
  const lastPlayTimestamp = useRef<number | null>(null);

  const shareUrl = window.location.href;

  useEffect(() => {
    if (!viewRecorded.current) {
      viewRecorded.current = true;
      onAnalytics('view');
    }
  }, [onAnalytics]);

  const trackPlay = useCallback(() => {
    lastPlayTimestamp.current = Date.now();
    if (!playRecorded.current) {
      playRecorded.current = true;
      onAnalytics('play');
    }
  }, [onAnalytics]);

  const trackPause = useCallback(() => {
    if (lastPlayTimestamp.current) {
      cumulativeWatchMs.current += Date.now() - lastPlayTimestamp.current;
      lastPlayTimestamp.current = null;
    }
  }, []);

  const trackComplete = useCallback(() => {
    trackPause();
    if (!completeRecorded.current) {
      completeRecorded.current = true;
      onAnalytics('complete', { watchDurationMs: cumulativeWatchMs.current, watchPercentage: 100 });
    }
  }, [onAnalytics, trackPause]);

  const trackShareClick = useCallback((platform: string) => {
    onAnalytics('share_click', {
      sharePlatform: platform,
      watchDurationMs: cumulativeWatchMs.current,
    });
  }, [onAnalytics]);

  const handleCopyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch {
      const inp = document.createElement('input');
      inp.value = shareUrl;
      document.body.appendChild(inp);
      inp.select();
      document.execCommand('copy');
      document.body.removeChild(inp);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
    trackShareClick('copy');
  };

  const embedCode = `<iframe src="${shareUrl}" width="360" height="640" frameborder="0" allow="autoplay; fullscreen" allowfullscreen style="border-radius:12px;max-width:100%;"></iframe>`;

  const handleCopyEmbed = async () => {
    try { await navigator.clipboard.writeText(embedCode); } catch {
      const ta = document.createElement('textarea');
      ta.value = embedCode;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2500);
    trackShareClick('embed');
  };

  const shareTitle = video.project_name
    ? `Check out "${video.project_name}" — created with ${config.appName}`
    : `Check out this video — created with ${config.appName}`;

  const socialActions = [
    { label: 'WhatsApp', platform: 'whatsapp', icon: SOCIAL_ICONS.whatsapp, onClick: () => window.open(`https://wa.me/?text=${encodeURIComponent(shareTitle + '\n' + shareUrl)}`, '_blank') },
    { label: 'Facebook', platform: 'facebook', icon: SOCIAL_ICONS.facebook, onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400') },
    { label: 'X (Twitter)', platform: 'twitter', icon: SOCIAL_ICONS.twitter, onClick: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400') },
    { label: 'Email', platform: 'email', icon: <Mail className="w-4 h-4" />, onClick: () => window.open(`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareTitle + '\n\n' + shareUrl)}`, '_self') },
  ];

  useEffect(() => {
    if (!showShareMenu) return;
    const close = () => setShowShareMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showShareMenu]);

  const gradientBg = { background: `linear-gradient(to right, ${effectivePrimary}, ${effectiveSecondary})` };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="px-4 py-3 sm:px-6 border-b border-slate-800/50 bg-black/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {effectiveLogoUrl ? (
              <img src={effectiveLogoUrl} alt="Brand" className={`${SHARE_LOGO_CLASS} object-left`} />
            ) : (
              <div className="w-[38px] h-[38px] sm:w-[43px] sm:h-[43px] rounded-lg flex items-center justify-center flex-shrink-0" style={gradientBg}>
                <Play className="w-4 h-4 sm:w-5 sm:h-5 text-white ml-0.5" />
              </div>
            )}
            <div className="min-w-0">
              {(agencyName || video.project_name) && (
                <h1 className="text-sm sm:text-base font-semibold text-white truncate">
                  {video.project_name || agencyName}
                </h1>
              )}
              {agencyName && video.project_name && (
                <p className="text-[11px] text-slate-400">{agencyName}</p>
              )}
            </div>
          </div>

          <div className="relative flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); setShowShareMenu(!showShareMenu); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-slate-700"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>

            {showShareMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
                <button onClick={handleCopyLink} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-800 transition-colors border-b border-slate-800">
                  {linkCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                  {linkCopied ? 'Link copied!' : 'Copy link'}
                </button>
                {isAdmin && config.showEmbedCode && (
                  <button onClick={handleCopyEmbed} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-800 transition-colors border-b border-slate-800">
                    {embedCopied ? <Check className="w-4 h-4 text-green-400" /> : <Code className="w-4 h-4 text-slate-400" />}
                    {embedCopied ? 'Embed copied!' : 'Copy embed code'}
                  </button>
                )}
                {showSocialShare && socialActions.map((action) => (
                  <button key={action.label} onClick={() => { trackShareClick(action.platform); action.onClick(); setShowShareMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-800 transition-colors">
                    <span className="text-slate-400">{action.icon}</span>
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Video Player */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-4 sm:py-6">
        <div className={`flex flex-col items-center w-full ${aspectMode === 'landscape' ? 'max-w-5xl' : ''}`}>
          <BrandedVideoPlayer
            videoUrl={video.video_url}
            primaryColor={effectivePrimary}
            secondaryColor={effectiveSecondary}
            aspectMode={aspectMode}
            audioTracks={video.audio_tracks}
            onPlay={trackPlay}
            onPause={trackPause}
            onEnded={trackComplete}
          />

          {/* Below-player actions */}
          <div className="mt-4 flex flex-col items-center gap-3 w-full" style={aspectMode === 'portrait' ? { maxWidth: 'min(420px, 100%)' } : undefined}>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <button onClick={handleCopyLink} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-slate-700">
                {linkCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {linkCopied ? 'Copied!' : 'Copy Link'}
              </button>
              {isAdmin && config.showEmbedCode && (
                <button onClick={handleCopyEmbed} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-slate-700">
                  {embedCopied ? <Check className="w-4 h-4 text-green-400" /> : <Code className="w-4 h-4" />}
                  {embedCopied ? 'Copied!' : 'Embed'}
                </button>
              )}
              {showSocialShare && socialActions.map((action) => (
                <button key={action.label} onClick={() => { trackShareClick(action.platform); action.onClick(); }} title={`Share on ${action.label}`} className="w-9 h-9 flex items-center justify-center bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700">
                  {action.icon}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 rounded-lg border border-slate-800 text-xs text-slate-500 w-full overflow-hidden">
              <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{shareUrl}</span>
            </div>
          </div>
        </div>
      </main>

      {/* CTA Banner */}
      <div className="px-4 py-6 sm:py-8 border-t border-slate-800/50" style={{ background: `linear-gradient(to right, ${effectivePrimary}15, ${effectiveSecondary}15, ${effectivePrimary}15)` }}>
        <div className="max-w-5xl mx-auto text-center">
          {effectiveCtaLabel && effectiveCtaUrl ? (
            <>
              <a href={effectiveCtaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg" style={gradientBg}>
                {effectiveCtaLabel}
                <ExternalLink className="w-4 h-4" />
              </a>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-3">{config.appTagline}</p>
              <a href={config.appUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg" style={gradientBg}>
                Create Your Own Video
                <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="px-4 py-5 sm:px-6 border-t border-slate-800/50 bg-black">
        <div className="max-w-5xl mx-auto flex flex-col items-center justify-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-600">Powered by</span>
          <a
            href={config.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-90 hover:opacity-100 transition-opacity"
          >
            {config.appLogoUrl ? (
              <img
                src={config.appLogoUrl}
                alt={config.appName}
                className={SHARE_LOGO_CLASS}
              />
            ) : (
              <span className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 transition-colors">
                <span className="w-5 h-5 rounded-md flex items-center justify-center" style={gradientBg}>
                  <Play className="w-2.5 h-2.5 text-white ml-px" />
                </span>
                <span className="font-semibold text-slate-300">{config.appName}</span>
              </span>
            )}
          </a>
        </div>
      </footer>
    </div>
  );
}

/** Loading state for the viewer */
export function BrandedViewerLoading({ primaryColor, secondaryColor }: { primaryColor: string; secondaryColor: string }) {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center animate-fade-in">
        <div className="w-12 h-12 mx-auto mb-4 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor})` }}>
          <Play className="w-6 h-6 text-white ml-0.5" />
        </div>
        <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
      </div>
    </div>
  );
}

/** Not-found state for the viewer */
export function BrandedViewerNotFound({
  appName,
  appUrl,
  appLogoUrl,
  primaryColor,
  secondaryColor,
}: {
  appName: string;
  appUrl: string;
  appLogoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
}) {
  return (
    <div className="min-h-screen bg-black flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center">
          <Play className="w-10 h-10 text-slate-700" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Video Not Found</h1>
        <p className="text-slate-400 text-sm mb-8 text-center max-w-sm">
          This link may have expired or the video has been removed.
        </p>
        <a
          href={appUrl}
          className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-all shadow-lg"
          style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
        >
          Create your own with {appName}
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      <footer className="px-4 py-5 sm:px-6 border-t border-slate-800/50 bg-black">
        <div className="max-w-5xl mx-auto flex flex-col items-center justify-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-600">Powered by</span>
          {appLogoUrl ? (
            <a href={appUrl} className="opacity-90 hover:opacity-100 transition-opacity">
              <img src={appLogoUrl} alt={appName} className={SHARE_LOGO_CLASS} />
            </a>
          ) : (
            <span className="text-xs text-slate-600">{appName}</span>
          )}
        </div>
      </footer>
    </div>
  );
}
